/**
 * Сторінка звернень до підтримки (ч.1009, Етап 3; макет власника 8.2).
 *
 * Дві колонки: ліворуч — список звернень (заголовок, `категорія · SUP-…`,
 * дата, бейдж статусу), праворуч — переписка. Підказані статті йдуть
 * ОКРЕМОЮ рамкою вгорі переписки, а не всередині повідомлення: вони
 * зʼявляються миттєво і мають шанс зняти питання ще до відповіді.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { support, type CaseStatus, type SupportCaseListItem } from '@/api/support';
import DocStatusBadge, { type BadgeTone } from '@/shared/components/DocStatusBadge';

const STATUS: Record<CaseStatus, { label: string; tone: BadgeTone }> = {
  NEW: { label: 'нове', tone: 'blue' },
  IN_PROGRESS: { label: 'в роботі', tone: 'blue' },
  WAITING_USER: { label: 'очікує вас', tone: 'amber' },
  ESCALATED: { label: 'передано розробнику', tone: 'amber' },
  RESOLVED: { label: 'вирішено', tone: 'green' },
  CLOSED: { label: 'закрито', tone: 'gray' },
};

function when(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('uk-UA',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function SupportPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [composing, setComposing] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [shotOf, setShotOf] = useState<string | null>(null);
  const feedRef = useRef<HTMLDivElement>(null);

  const list = useQuery({
    queryKey: ['support-cases'],
    queryFn: support.cases,
    refetchInterval: 60_000,
  });

  const active = useQuery({
    queryKey: ['support-case', activeId],
    queryFn: () => support.case(activeId!),
    enabled: !!activeId,
    // Поки чекаємо на відповідь — оновлюємо переписку (макет: 15 с).
    refetchInterval: 15_000,
  });

  // Перше звернення відкриваємо самі: порожня права колонка виглядає
  // як помилка, а не як стан «нічого не обрано».
  useEffect(() => {
    if (!activeId && list.data?.length) setActiveId(list.data[0]!.id);
  }, [list.data, activeId]);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight });
  }, [active.data?.messages.length]);

  const create = useMutation({
    // ч.1013 (рішення власника): у модалці знімка НЕМАЄ — він доречний
    // лише у віджеті, коли людина стоїть на проблемній сторінці.
    mutationFn: () => support.create({
      title: newTitle.trim(),
      body: newBody.trim(),
      // Сторінка, з якої прийшли, — підказки точніші, а власник бачить,
      // де довідки бракує.
      route_pattern: sessionStorage.getItem('support:lastRoute'),
    }),
    onSuccess: (c) => {
      setComposing(false); setNewTitle(''); setNewBody('');
      setActiveId(c.id);
      qc.invalidateQueries({ queryKey: ['support-cases'] });
    },
  });

  const reply = useMutation({
    mutationFn: () => support.reply(activeId!, draft.trim()),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['support-case', activeId] });
      qc.invalidateQueries({ queryKey: ['support-cases'] });
    },
  });

  const close = useMutation({
    mutationFn: () => support.close(activeId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-case', activeId] });
      qc.invalidateQueries({ queryKey: ['support-cases'] });
    },
  });

  const cases: SupportCaseListItem[] = useMemo(() => list.data ?? [], [list.data]);
  const doc = active.data;
  const canClose = doc && doc.status !== 'CLOSED';

  return (
    <div className="card sup">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Підтримка</h2>
        <button type="button" className="primary"
          onClick={() => setComposing(true)}>
          почати діалог
        </button>
      </div>

      <div className="sup__body">
        <aside className="sup__list">
          {list.isLoading && <div className="sup__muted">Завантаження…</div>}
          {!list.isLoading && cases.length === 0 && !composing && (
            <div className="sup__muted">
              Звернень поки немає. Натисніть «почати діалог», якщо потрібна
              допомога.
            </div>
          )}
          {cases.map((c) => (
            <button key={c.id} type="button"
              className={`sup__card${c.id === activeId ? ' active' : ''}`}
              onClick={() => setActiveId(c.id)}>
              <div className="sup__card-head">
                <span className="sup__card-title">{c.title}</span>
                {/* ч.1241: відповідь, якої ще не читали (поточну переписку вже видно) */}
                {(c.unread ?? 0) > 0 && c.id !== activeId && (
                  <span className="sup__unread" title="Нові відповіді підтримки">{c.unread}</span>
                )}
                <DocStatusBadge {...STATUS[c.status]} />
              </div>
              <div className="sup__card-meta">
                {c.category ? `${c.category} · ` : ''}{c.public_id}
              </div>
              <div className="sup__card-meta">{when(c.last_message_at)}</div>
            </button>
          ))}
        </aside>

        <section className="sup__feed">
          {!doc && (
            <div className="sup__muted">Оберіть звернення зі списку.</div>
          )}

          {doc && (
            <>
              <div className="sup__head">
                <div>
                  <div className="sup__head-id">{doc.public_id}</div>
                  <div className="sup__head-title">{doc.title}</div>
                </div>
                <DocStatusBadge {...STATUS[doc.status]} />
              </div>

              {doc.suggested.length > 0 && (
                <div className="sup__hints">
                  <div className="sup__hints-title">
                    Можливо, ви знайдете відповідь у цих довідкових матеріалах
                  </div>
                  {doc.suggested.map((a) => (
                    <button key={a.slug} type="button" className="sup__hint"
                      onClick={() => navigate(`/help/${a.slug}`)}>
                      <span className="sup__hint-title">{a.title}</span>
                      {a.summary && (
                        <span className="sup__hint-sum">{a.summary}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div className="sup__msgs" ref={feedRef}>
                {doc.messages.map((m) => (
                  <div key={m.id}
                    className={`sup__msg${m.author_kind === 'user' ? ' own' : ''}`}>
                    <div className="sup__msg-head">
                      <span>{m.author_kind === 'user'
                        ? (m.author_name || 'Ви') : 'Служба підтримки'}</span>
                      <span>{when(m.created_at)}</span>
                    </div>
                    <div className="sup__msg-body">{m.body}</div>
                    {m.has_screenshot && (
                      /* ч.1012: знімок важкий, тому вантажимо лише за
                         кліком — переписка оновлюється раз на 15 с. */
                      <button type="button" className="sup__shot"
                        onClick={() => setShotOf(m.id)}>
                        знімок екрана
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="sup__reply">
                <textarea value={draft} rows={3} maxLength={8000}
                  placeholder="Ваше повідомлення…"
                  onChange={(e) => setDraft(e.target.value)} />
                <div className="sup__actions">
                  {canClose && (
                    <button type="button" disabled={close.isPending}
                      onClick={() => close.mutate()}>
                      питання вирішено
                    </button>
                  )}
                  <button type="button" className="primary"
                    disabled={!draft.trim() || reply.isPending}
                    onClick={() => reply.mutate()}>
                    {reply.isPending ? 'надсилаю…' : 'надіслати'}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {composing && (
        /* Модальне вікно, як на прикладі власника: заголовок + текст +
           застереження про секрети. Форма НЕ вбудовується у колонку —
           саме це було зауваженням до ч.1010. */
        <div className="sup__overlay" onClick={() => setComposing(false)}>
          <div className="card sup__modal" onClick={(e) => e.stopPropagation()}>
            <div className="sup__modal-head">
              <h3>Почати діалог</h3>
              <button type="button" className="sup__modal-x"
                aria-label="Закрити" onClick={() => setComposing(false)}>✕</button>
            </div>
            <label className="field">
              <span>Заголовок</span>
              <input value={newTitle} maxLength={200} autoFocus
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Коротко опишіть питання" />
            </label>
            <label className="field">
              <span>Повідомлення</span>
              <textarea value={newBody} rows={7} maxLength={8000}
                onChange={(e) => setNewBody(e.target.value)}
                placeholder="Що сталося і який результат ви очікували" />
            </label>

            <div className="sup__actions">
              <button type="button" onClick={() => setComposing(false)}>
                скасувати
              </button>
              <button type="button" className="primary"
                disabled={newTitle.trim().length < 3
                  || newBody.trim().length < 3 || create.isPending}
                onClick={() => create.mutate()}>
                {create.isPending ? 'надсилаю…' : 'надіслати'}
              </button>
            </div>
          </div>
        </div>
      )}
      {shotOf && <ScreenshotView messageId={shotOf}
                                 onClose={() => setShotOf(null)} />}
    </div>
  );
}

/** Перегляд знімка екрана з повідомлення (ч.1012). */
function ScreenshotView({ messageId, onClose }:
  { messageId: string; onClose: () => void }) {
  const shot = useQuery({
    queryKey: ['support-shot', messageId],
    queryFn: () => support.screenshot(messageId),
  });
  return (
    <div className="sup__overlay" onClick={onClose}>
      <div className="card sup__shotbox" onClick={(e) => e.stopPropagation()}>
        <div className="sup__modal-head">
          <h3>Знімок екрана</h3>
          <button type="button" className="sup__modal-x" aria-label="Закрити"
            onClick={onClose}>✕</button>
        </div>
        {shot.isLoading && <div className="sup__muted">Завантаження…</div>}
        {shot.isError && <div className="sup__muted">Знімок недоступний.</div>}
        {shot.data && <img src={shot.data} alt="Знімок екрана"
                           className="sup__shotimg" />}
      </div>
    </div>
  );
}
